// Leagues: every configured league grouped by sport. Each card shows the league's
// identity (code, name, country, season, format), its size (members / picks /
// rounds) and the per-tier member distribution. Tapping a card opens its detail.
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cap } from '../../lib/format';
import { LeagueMark } from '../../components/ui/bits';
import type { AdminLeagueRow } from '../../lib/adminApi';
import { EmptyState, PageTitle, Pill, TierBar, useAdminData } from './parts';

export default function Leagues() {
  const { data } = useAdminData();
  const leagues = [...data.leagues].sort((a, b) => b.memberCount - a.memberCount);

  const bySport = new Map<string, AdminLeagueRow[]>();
  for (const l of leagues) {
    const arr = bySport.get(l.sport) ?? [];
    arr.push(l);
    bySport.set(l.sport, arr);
  }

  return (
    <>
      <PageTitle
        title="Leagues"
        sub={`${leagues.length} leagues · ${data.stats.totalMemberships} memberships`}
      />

      {leagues.length === 0 ? (
        <EmptyState>No leagues configured.</EmptyState>
      ) : (
        <div className="space-y-7">
          {[...bySport.entries()].map(([sport, list]) => (
            <section key={sport}>
              <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-eyebrow text-ink2">
                {cap(sport)} · {list.length}
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                {list.map((l) => (
                  <LeagueCard key={l.leagueId} league={l} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}

function LeagueCard({ league }: { league: AdminLeagueRow }) {
  const code = (league.shortCode ?? league.name.slice(0, 3)).toUpperCase();
  const meta = [league.country, `Season ${league.season}`, cap(league.format)].filter(Boolean).join(' · ');
  return (
    <Link
      to={`/admin/leagues/${league.leagueId}`}
      className="tap group block rounded-2xl border border-black/5 bg-card p-4 shadow-tile transition-colors hover:border-black/10 md:p-5"
    >
      <div className="flex items-start gap-3">
        <LeagueMark leagueId={league.leagueId} country={league.country} code={code} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate text-[15px] font-extrabold tracking-tight">{league.name}</div>
            {league.currentRoundId ? <Pill tone="accent">Active</Pill> : <Pill>Idle</Pill>}
          </div>
          <div className="mt-0.5 truncate text-[12px] text-ink2">{meta}</div>
        </div>
        <ChevronRight size={18} className="mt-1 shrink-0 text-ink3 transition-transform group-hover:translate-x-0.5" />
      </div>

      <div className="mt-3 flex items-center gap-4 text-[12px] text-ink2">
        <span>
          <span className="num font-bold text-ink">{league.memberCount}</span> members
        </span>
        <span>
          <span className="num font-bold text-ink">{league.entriesCount}</span> picks
        </span>
        <span>
          <span className="num font-bold text-ink">{league.roundsCovered}</span> rounds
        </span>
      </div>

      <div className="mt-3">
        <TierBar tiers={league.tierCounts} />
      </div>
    </Link>
  );
}
